import * as RadixPopover from "@radix-ui/react-popover";
import { CaretDown, Check, X } from "@phosphor-icons/react";
import { forwardRef, Fragment, type KeyboardEvent, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfContentIn, twTransform } from "@/lib/emotionTw";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding } from "@/components/ui/overlays/overlayPositioning";
import { Spinner } from "../../feedback/Spinner";
import { groupSelectOptions, type SelectMenuWidth, type SelectOption } from "../Select";

export type SearchableSelectProps = {
  options: SelectOption[];
  value?: string;
  onValueChange?: (value: string | undefined) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  clearable?: boolean;
  size?: "sm" | "md";
  /** Option popover width relative to the trigger. Defaults to `"auto"`. */
  menuWidth?: SelectMenuWidth;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

type SearchableSelectSize = "sm" | "md";

interface SearchableSelectTriggerOwnerState {
  size: SearchableSelectSize;
  error: boolean;
  hasSelection: boolean;
}

/* Styles are the exact compiled output of the pre-migration Tailwind
 * utilities, emitted in the original class-list order. Verified against the
 * frozen baseline build, except the trigger active ring/outline was removed so
 * focused/open select fields render a single border. */

const ssTriggerBase: CSSObject = {
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
  gap: ".5rem",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  textAlign: "left",
  fontWeight: 400,
  outline: "none",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:disabled": {
    cursor: "not-allowed",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    WebkitTextFillColor: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  }
};

const ssTriggerSize: Record<SearchableSelectSize, CSSObject> = {
  sm: { borderRadius: "7px", paddingLeft: ".75rem", paddingRight: ".75rem", fontSize: "12px", height: "1.75rem" },
  md: { borderRadius: "7px", paddingLeft: ".75rem", paddingRight: ".75rem", fontSize: "12px", height: "2.25rem" }
};

const ssTriggerError: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
  "&:focus-visible": {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  },
  '&[data-state="open"]': {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  },
  '&[aria-expanded="true"]': {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  }
};

const ssTriggerDefaultBorder = (size: SearchableSelectSize): CSSObject => ({
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "&:focus-visible": {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  '&[data-state="open"]': {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  '&[aria-expanded="true"]': {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  ...(size === "sm"
    ? {
        "&[data-filled]": {
          borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
        }
      }
    : null)
});

const SearchableSelectTrigger = styled("button", {
  label: "AgenticSearchableSelect-trigger",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: SearchableSelectTriggerOwnerState }>(({ ownerState }) => [
  ssTriggerBase,
  ssTriggerSize[ownerState.size],
  ownerState.error ? ssTriggerError : ssTriggerDefaultBorder(ownerState.size),
  ownerState.hasSelection
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-text-opacity": "1",
        color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
      }
]);

const SearchableSelectTruncate = styled("span", {
  label: "AgenticSearchableSelect-truncate"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const SearchableSelectControls = styled("span", {
  label: "AgenticSearchableSelect-controls"
})({
  display: "flex",
  alignItems: "center",
  gap: ".25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const SearchableSelectClear = styled("span", {
  label: "AgenticSearchableSelect-clear"
})({
  borderRadius: "var(--bfrs-radius-sm)",
  padding: ".125rem",
  "&:hover": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
  }
});

const SearchableSelectCaret = styled(CaretDown, {
  label: "AgenticSearchableSelect-caret",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { open: boolean } }>(({ ownerState }) => [
  {
    transitionProperty: "transform",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.open && twTransform({ "--tw-rotate": "180deg" })
]);

const SearchableSelectContent = styled(RadixPopover.Content, {
  label: "AgenticSearchableSelect-content",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { menuWidth: SelectMenuWidth } }>(({ ownerState }) => [
  {
    zIndex: 1000,
    borderRadius: "var(--bfrs-radius-lg)",
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    padding: ".25rem",
    "--tw-shadow": "var(--bfrs-shadow-elevated)",
    "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    ...kfContentIn,
    animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
  },
  ownerState.menuWidth === "trigger" && { width: "var(--radix-popover-trigger-width)" },
  ownerState.menuWidth === "auto" && { minWidth: "var(--radix-popover-trigger-width)" },
  // searchableSelectContentCollisionClassName: always sets the trigger width
  // (a numeric menuWidth wins via inline style, exactly as before).
  {
    maxHeight: "min(18rem,var(--radix-popover-content-available-height))",
    width: "var(--radix-popover-trigger-width)",
    overflow: "hidden"
  }
]);

const SearchableSelectSearch = styled("input", {
  label: "AgenticSearchableSelect-search"
})({
  marginBottom: ".25rem",
  height: "1.75rem",
  width: "100%",
  borderRadius: "7px",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  fontSize: "12px",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  "&::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&::placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  outline: "none",
  "&:focus": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  "&:focus-visible": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  }
});

// searchableSelectListboxCollisionClassName, inlined.
const SearchableSelectListbox = styled("div", {
  label: "AgenticSearchableSelect-listbox"
})({
  maxHeight: "min(14rem,max(4rem,calc(var(--radix-popover-content-available-height) - 3rem)))",
  overflow: "auto"
});

const SearchableSelectLoading = styled("div", {
  label: "AgenticSearchableSelect-loading"
})({
  display: "flex",
  height: "4rem",
  alignItems: "center",
  justifyContent: "center",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const SearchableSelectEmpty = styled("div", {
  label: "AgenticSearchableSelect-empty"
})({
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingTop: "1rem",
  paddingBottom: "1rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const SearchableSelectGroupLabel = styled("div", {
  label: "AgenticSearchableSelect-groupLabel"
})({
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingBottom: ".25rem",
  paddingTop: ".5rem",
  fontSize: "10px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".08em",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

interface SearchableSelectOptionOwnerState {
  selected: boolean;
  active: boolean;
}

const SearchableSelectOption = styled("button", {
  label: "AgenticSearchableSelect-option",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: SearchableSelectOptionOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    width: "100%",
    cursor: "pointer",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: "var(--bfrs-radius-sm)",
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    paddingTop: ".5rem",
    paddingBottom: ".5rem",
    textAlign: "left",
    fontSize: "12px",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
    },
    "&:disabled": {
      cursor: "not-allowed",
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
    }
  },
  ownerState.selected && {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
    fontWeight: 700
  },
  ownerState.active && {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
  }
]);

const SearchableSelectCheckIcon = styled(Check, {
  label: "AgenticSearchableSelect-checkIcon"
})({
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

export const SearchableSelect = forwardRef<HTMLButtonElement, SearchableSelectProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = "Select",
      searchPlaceholder = "Search...",
      emptyText = "No results found",
      loading,
      disabled,
      error,
      clearable = true,
      size = "md",
      menuWidth = "auto",
      className,
      id,
      "aria-describedby": describedBy,
      "aria-invalid": ariaInvalid
    },
    ref
  ) => {
    const menuWidthStyle = typeof menuWidth === "number" ? { width: menuWidth, minWidth: menuWidth } : undefined;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const selected = options.find((option) => option.value === value);
    const filteredOptions = useMemo(
      () => options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase())),
      [options, query]
    );

    const handleOpenChange = (nextOpen: boolean) => {
      setOpen(nextOpen);

      if (!nextOpen) {
        setQuery("");
        setActiveIndex(0);
      }
    };

    const commitValue = (nextValue: string | undefined) => {
      onValueChange?.(nextValue);
      handleOpenChange(false);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
      if (!open && ["ArrowDown", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        handleOpenChange(true);
        return;
      }
      if (!open) return;
      if (event.key === "Escape") handleOpenChange(false);
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
      }
      if (event.key === "Enter" && filteredOptions[activeIndex]) {
        event.preventDefault();
        commitValue(filteredOptions[activeIndex].value);
      }
    };

    return (
      <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
        <RadixPopover.Trigger asChild>
          <SearchableSelectTrigger
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? error}
            data-filled={selected && !error ? "" : undefined}
            ownerState={{ size, error: Boolean(error), hasSelection: Boolean(selected) }}
            className={className}
            onKeyDown={handleKeyDown}
          >
            <SearchableSelectTruncate>{selected?.label ?? placeholder}</SearchableSelectTruncate>
            <SearchableSelectControls>
              {clearable && selected && (
                <SearchableSelectClear
                  role="button"
                  tabIndex={-1}
                  aria-label="Clear selection"
                  onClick={(event) => {
                    event.stopPropagation();
                    commitValue(undefined);
                  }}
                >
                  <X size={14} />
                </SearchableSelectClear>
              )}
              <SearchableSelectCaret ownerState={{ open }} size={10} />
            </SearchableSelectControls>
          </SearchableSelectTrigger>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <SearchableSelectContent
              align="start"
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                inputRef.current?.focus();
              }}
              onKeyDown={handleKeyDown}
              style={menuWidthStyle}
              ownerState={{ menuWidth }}
            >
              <SearchableSelectSearch
                ref={inputRef}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActiveIndex(0);
                }}
                placeholder={searchPlaceholder}
              />
              <SearchableSelectListbox role="listbox">
                {loading ? (
                  <SearchableSelectLoading>
                    <Spinner size="sm" />
                  </SearchableSelectLoading>
                ) : filteredOptions.length === 0 ? (
                  <SearchableSelectEmpty>{emptyText}</SearchableSelectEmpty>
                ) : (
                  (() => {
                    let flatIndex = -1;
                    return groupSelectOptions(filteredOptions).map((grp, groupIndex) => (
                      <Fragment key={`group-${groupIndex}`}>
                        {grp.group && <SearchableSelectGroupLabel>{grp.group}</SearchableSelectGroupLabel>}
                        {grp.items.map((option) => {
                          flatIndex += 1;
                          const index = flatIndex;
                          return (
                            <SearchableSelectOption
                              key={option.value}
                              type="button"
                              role="option"
                              aria-selected={option.value === value}
                              disabled={option.disabled}
                              ownerState={{ selected: option.value === value, active: activeIndex === index }}
                              onMouseEnter={() => setActiveIndex(index)}
                              onClick={() => commitValue(option.value)}
                            >
                              {option.label}
                              {option.value === value && <SearchableSelectCheckIcon size={16} weight="bold" />}
                            </SearchableSelectOption>
                          );
                        })}
                      </Fragment>
                    ));
                  })()
                )}
              </SearchableSelectListbox>
            </SearchableSelectContent>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  }
);

SearchableSelect.displayName = "SearchableSelect";
